import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {catchError, map, Observable, throwError} from 'rxjs';
import {DomSanitizer} from '@angular/platform-browser';
import {environment} from '@/environments/environment';
import {PersonEntity} from '@/app/shared/models/person-model';
import {
    Class,
    ConfigClassImage,
    ConfigCourseInsert, ConfigCourseInvoiceDto, ConfigCourseUnitPriceDto,
    ConfigModule,
    Course,
    Exam,
    ExamResult,
    Question,
    QuestionOption,
    Reference,
    StudentFeedback
} from '@/app/shared/models/course-model';
import {CourseRequest} from '@/app/shared/models/course-request-model';
import {ApiCodeMessage} from '@/app/shared/consts/api-code-message.constant';
import {InvoiceDetailDto, InvoiceDto} from "@/app/shared/models/invoice-model";


@Injectable({
    providedIn: 'root'
})
export class FeaturesService {

    constructor(private _http: HttpClient, private domSanitizer: DomSanitizer) {
    }

    handleServiceError(error: HttpErrorResponse) {
        let mensajeError = undefined;
        if (error && error.error && error.error.error && typeof error.error.error === 'string') {
            mensajeError = error.error.error;
        } else if (error.error instanceof ErrorEvent) {
            console.error('Error:', error.error.message);
            mensajeError = `Error: ${error.error.message}`;
        } else {
            switch (error.status) {
                case 400:
                    console.error(`Error del backend, código: ${error.status}, `);
                    mensajeError = error.error.error;
                    break;
                case 403:
                    console.error(`Error del backend, código: ${error.status}, `);
                    mensajeError = error.error.error;
                    break;
                case 500:
                    console.error(`Error del backend, código: ${error.status} `);
                    mensajeError = error.error ? error.error : error;
                    break;
                case 503:
                    console.error(`Error del backend, código: ${error.status} `);
                    mensajeError = ApiCodeMessage.MSG_CODE_503;
                    break;
                default:
                    console.error(`Error: ${error} `);
                    mensajeError = error;
                    break;
            }
        }
        if (typeof mensajeError !== 'string') {
            mensajeError = '';
        }
        return throwError(() => mensajeError);
    }

    updateConfigCourseUnitPrice(unitPrice: ConfigCourseUnitPriceDto): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceConfigCourseUnitPrice, unitPrice, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getAllConfigCourseUnitPrice(courseId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigCourseUnitPrice + `/by-course/${courseId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getAllConfigCourseInvoice(courseId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigCourseInvoice + `/by-course/${courseId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getConfigCourseInvoiceById(courseInvoiceId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigCourseInvoice + `/${courseInvoiceId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    updateConfigCourseInvoice(courseInvoiceDto: ConfigCourseInvoiceDto): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceConfigCourseInvoice, courseInvoiceDto, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    insertConfigCourseInvoice(courseInvoiceDto: ConfigCourseInvoiceDto): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceConfigCourseInvoice, courseInvoiceDto, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteConfigCourseInvoice(courseInvoiceId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceConfigCourseInvoice + `/${courseInvoiceId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    getAllConfigCoursesSimple(): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigCourse + `/simple`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getConfigCourse(courseId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigCourse + `/${courseId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener todos los cursos publicos y privaos de las entidades suscripta */
    getAllCourses(personId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigCourse + `/home/by-person/${personId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateConfigCourse(configCourse: ConfigCourseInsert, file?: File | null): Observable<any> {
        const formData = new FormData();
        formData.append(
            'dto',
            new Blob([JSON.stringify(configCourse)], {type: 'application/json'})
        );

        if (file) {
            formData.append('logoFile', file);
        }

        return this._http
            .put<any>(environment.serviceConfigCourse, formData, {
                headers: new HttpHeaders({accept: '*/*'}),
                responseType: 'json'
            })
            .pipe(
                catchError(this.handleServiceError)
            );
    }

    insertConfigCourse(configCourse: ConfigCourseInsert, file?: File | null): Observable<any> {
        const formData = new FormData();
        formData.append(
            'dto',
            new Blob([JSON.stringify(configCourse)], {type: 'application/json'})
        );

        if (file) {
            formData.append('logoFile', file);
        }

        return this._http
            .post<any>(environment.serviceConfigCourse, formData, {
                headers: new HttpHeaders({accept: '*/*'}),
                responseType: 'json'
            })
            .pipe(
                catchError(this.handleServiceError)
            );
    }

    updateStatusConfigCourse(courseId?: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceConfigCourse + `/${courseId}`, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteCourse(courseId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceConfigCourse + `/${courseId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    /** Valida un certificado por su record locator, es publico (sin token) */
    verifyCertificate(certifyCode: string): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceCertificate + '/verify/' + encodeURIComponent(certifyCode), {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getAllConfigCoursesLanding(): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigCourse + `/landing-page`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getAllConfigCoursesTeacher(teacherId: number, pageNumber: number, pageSize: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        let queryParams = new HttpParams();
        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceConfigCourse + `/by-teacher/${teacherId}`, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener todos los cursos mixtos */
    getAllCoursesMixed(): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigCourseMixed, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener todos los cursos propios por entidad */
    getAllOwnerCourseByEntityId(entityId: number, pageNumber: number, pageSize: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        let queryParams = new HttpParams();
        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceConfigCourse + `/by-entity/${entityId}`, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener todos los cursos propios por entidad */
    getAllOwnerCourseByEntityIdAndIsFreeFalse(entityId: number, pageNumber: number, pageSize: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        let queryParams = new HttpParams();
        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceConfigCourse + `/by-entity-free-false/${entityId}`, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener todos los cursos propios por entidad */
    getAllVisibleCourseByEntityId(entityId: number, personId: number, pageNumber: number, pageSize: number, searchParam?: string): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        let queryParams = new HttpParams().append('personId', personId);
        searchParam !== undefined
            ? (queryParams = queryParams.append('searchParam', searchParam))
            : null;
        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceConfigCourse + `/by-entity-visible/${entityId}`, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener cursos por persona */
    getStudentCoursesByPerson(personId: number, pageNumber: number, pageSize: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        let queryParams = new HttpParams();
        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceStudentCourse + `/by-person/${personId}`, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getStudentCourseByPerson(personId: number, pageNumber: number, pageSize: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        let queryParams = new HttpParams();
        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceStudentCourse + `/active-by-person/` + `${personId}`, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getPersonById(personId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .get<any>(environment.servicePerson + `/${personId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getAllPersonSimple(searchParam: string): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();
        searchParam !== undefined
            ? (queryParams = queryParams.append('searchParam', searchParam))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.servicePerson + `/simple`, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    uploadInvoice(personId: number, courseId: number, file: File): Observable<Course> {
        const formData = new FormData();
        formData.append('personId', personId.toString());
        formData.append('courseId', courseId.toString());
        formData.append(
            'invoice',
            file,
            file.name || 'invoice.pdf'
        );
        return this._http.post<Course>(environment.serviceStudentCourse + '/upload_invoice', formData);
    }

    uploadCertify(personId: number, courseId: number, file: File): Observable<Course> {
        const formData = new FormData();
        formData.append('personId', personId.toString());
        formData.append('courseId', courseId.toString());
        // formData.append('certify', file);
        formData.append(
            'certify',
            file,
            file.name || 'payment.jpg'
        );
        return this._http.post<Course>(environment.serviceStudentCourse + '/upload_certify', formData);
    }

    regenerateCertify(courseId: number): Observable<any> {
        let queryParams = new HttpParams();
        queryParams = queryParams.append('courseId', courseId);
        return this._http
            .post<any>(environment.serviceStudentCourse + '/regenerate_certify' + '?' + queryParams, null)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    downloadCertify(filename: string): Observable<Blob> {
        const options = {
            responseType: 'blob' as const,
            params: {filename}
        };
        return this._http.get(environment.serviceImagenes + '/pdf', options);
    }

    applyCourse(formData: FormData): Observable<any> {
        const headers = new HttpHeaders({
            accept: '*/*',
        });

        return this._http
            .post<any>(environment.serviceApplyCourse, formData, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    applyContractCourse(formData: FormData): Observable<any> {
        const headers = new HttpHeaders({
            accept: '*/*',
        });

        return this._http
            .post<any>(environment.serviceApplyContractCourse, formData, {
                headers: headers,
                responseType: "blob" as "json"
            })
            .pipe(
                map(data => this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(data))),
                catchError(this.handleServiceError)
            );
    }

    activeCourse(personId?: number, courseId?: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();

        personId != null
            ? (queryParams = queryParams.append('personId', personId))
            : null;

        courseId != null
            ? (queryParams = queryParams.append('courseId', courseId))
            : null;

        return this._http
            .post<any>(environment.serviceStudentCourse + '/active' + '?' + queryParams, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    rejectCourse(personId?: number, courseId?: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();

        personId != null
            ? (queryParams = queryParams.append('personId', personId))
            : null;

        courseId != null
            ? (queryParams = queryParams.append('courseId', courseId))
            : null;

        return this._http
            .post<any>(environment.serviceStudentCourse + '/reject' + '?' + queryParams, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    reactivateStudentCourse(personId?: number, courseId?: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();

        personId != null
            ? (queryParams = queryParams.append('personId', personId))
            : null;

        courseId != null
            ? (queryParams = queryParams.append('courseId', courseId))
            : null;

        return this._http
            .post<any>(environment.serviceStudentCourse + '/reactivate' + '?' + queryParams, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Editar perfil */
    editPerson(reqUser: PersonEntity, personId: number, file?: File | null): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        const formData = new FormData();
        formData.append(
            'dto',
            new Blob([JSON.stringify(reqUser)], {type: 'application/json'})
        );

        if (file) {
            formData.append('logoFile', file);
        }

        return this._http
            .put<any>(environment.servicePerson + `/${personId}`, formData, {
                headers: new HttpHeaders({accept: '*/*'}),
                responseType: 'json'
            })
            .pipe(
                catchError(this.handleServiceError)
            );
    }

    /** Crear persona desde administracion (lleva token, a diferencia del registro publico) */
    createPerson(reqUser: PersonEntity, file?: File | null): Observable<any> {
        const formData = new FormData();
        formData.append(
            'dto',
            new Blob([JSON.stringify(reqUser)], {type: 'application/json'})
        );

        if (file) {
            formData.append('logoFile', file);
        }

        return this._http
            .post<any>(environment.servicePerson, formData, {
                headers: new HttpHeaders({accept: '*/*'}),
                responseType: 'json'
            })
            .pipe(
                catchError(this.handleServiceError)
            );
    }

    getCourses(request: CourseRequest, pageNumber: number, pageSize: number, sort: string): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();

        request.searchParam !== undefined
            ? (queryParams = queryParams.append('searchParam', request.searchParam))
            : null;
        request.status !== undefined
            ? (queryParams = queryParams.append('status', request.status))
            : null;
        request.teacherId !== undefined
            ? (queryParams = queryParams.append('teacherId', request.teacherId))
            : null;
        request.courseId !== undefined
            ? (queryParams = queryParams.append('courseId', request.courseId))
            : null;
        request.courseName !== undefined
            ? (queryParams = queryParams.append('courseName', request.courseName))
            : null;
        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;
        sort !== undefined
            ? (queryParams = queryParams.append('sort', sort))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceStudentCourse, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getAllInvoices(request: string, pageNumber: number, pageSize: number, entityId?: number, paid?: boolean): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();

        request !== undefined
            ? (queryParams = queryParams.append('searchParam', request))
            : null;

        entityId !== undefined
            ? (queryParams = queryParams.append('entityId', entityId))
            : null;

        paid !== undefined
            ? (queryParams = queryParams.append('paid', paid))
            : null;

        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceInvoice, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    findEntitiesWithDebtSummaryInvoice(pageNumber: number, pageSize: number, request?: string): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();

        request !== undefined
            ? (queryParams = queryParams.append('searchParam', request))
            : null;

        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceInvoice + `/entities-with-debt`, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getInvoiceById(invoiceId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceInvoice + `/${invoiceId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    previewInvoice(invoiceId: number): Observable<Blob> {
        return this._http.get(environment.serviceInvoice + `/show-pdf/${invoiceId}`, {
            headers: new HttpHeaders({ accept: 'application/pdf' }),
            responseType: 'blob'
        });
    }

    generateAndSendEmail(invoiceId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceInvoice + `/generateAndSendEmail/${invoiceId}`, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateAndGenerateInvoiceForEntity(invoiceId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceInvoice + `/update-invoice/${invoiceId}`, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateInvoice(invoiceDto: InvoiceDto): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceInvoice, invoiceDto, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    insertInvoice(invoiceDto: InvoiceDto): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceInvoice, invoiceDto, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteInvoice(invoiceId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceInvoice + `/${invoiceId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    getInvoiceSendHistory(invoiceId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceInvoiceSendHistory + `/by-invoice/${invoiceId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    getAllTraces(request: string, pageNumber: number, pageSize: number, sort: string, startDate?: string, endDate?: string): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();

        request !== undefined
            ? (queryParams = queryParams.append('searchParam', request))
            : null;

        startDate !== undefined
            ? (queryParams = queryParams.append('startDate', startDate))
            : null;

        endDate !== undefined
            ? (queryParams = queryParams.append('endDate', endDate))
            : null;

        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;
        sort !== undefined
            ? (queryParams = queryParams.append('sort', sort))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.serviceTrace, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getPersons(request: string, pageNumber: number, pageSize: number, sort: string, roleId?: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        let queryParams = new HttpParams();

        request !== undefined
            ? (queryParams = queryParams.append('searchParam', request))
            : null;

        roleId !== undefined
            ? (queryParams = queryParams.append('roleId', roleId))
            : null;

        pageNumber !== undefined
            ? (queryParams = queryParams.append('pageNumber', pageNumber))
            : null;
        pageSize !== undefined
            ? (queryParams = queryParams.append('pageSize', pageSize))
            : null;
        sort !== undefined
            ? (queryParams = queryParams.append('sort', sort))
            : null;

        const options = {
            headers: headers,
            params: queryParams,
        };

        return this._http
            .get<any>(environment.servicePerson, options)
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener modulos de un curso */
    getCourseModulesByCourseId(courseId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceStudentModule + `/by-course/${courseId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener todos los modulos config de un curso */
    getAllConfigModulesByCourseId(courseId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigModule + `/by-course/${courseId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener todos los modulos comunes */
    findAllByIsCommonTrue(): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigModule + `/is-common`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    associateModuleToCourse(formData: FormData): Observable<any> {
        const headers = new HttpHeaders({
            accept: '*/*',
        });

        return this._http
            .post<any>(environment.serviceConfigModule + `/associate-to-course`, formData, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getConfigModule(moduleId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigModule + `/${moduleId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateConfigModule(configModule: ConfigModule): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceConfigModule, configModule, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    insertConfigModule(configModule: ConfigModule): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceConfigModule, configModule, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteModule(moduleId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceConfigModule + `/${moduleId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    getConfigClasses(moduleId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigClass + `/by-module/${moduleId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getConfigClass(classId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigClass + `/${classId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateConfigClass(configClass: Class): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceConfigClass, configClass, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    insertConfigClass(configClass: Class): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceConfigClass, configClass, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteClass(classId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceConfigClass + `/${classId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    importConfigClassImagenAndAudios(classConfig: ConfigClassImage, zipFile?: File | null): Observable<any> {
        const formData = new FormData();
        formData.append('dto', new Blob([JSON.stringify(classConfig)], {type: 'application/json'}));

        if (zipFile) {
            formData.append('zipFile', zipFile, zipFile.name);
        }

        return this._http
            .post<any>(environment.serviceConfigClassImage, formData, {
                headers: new HttpHeaders({accept: '*/*'})
            })
            .pipe(
                catchError(this.handleServiceError)
            );
    }

    importExamQuestionsAndImages(examId: number, docFile: File, zipFile?: File): Observable<any> {
        const formData = new FormData();

        formData.append('word', docFile, docFile.name);
        if (zipFile) {
            formData.append('zipFile', zipFile, zipFile.name);
        }

        return this._http
            .post<any>(environment.serviceConfigExamQuestion + `/import-exam/${examId}`, formData, {
                headers: new HttpHeaders({accept: '*/*'})
            })
            .pipe(
                catchError(this.handleServiceError)
            );
    }

    changeClassImage(classImageId: number, image: File): Observable<any> {
        const formData = new FormData();
        formData.append('image', image, image.name);

        return this._http
            .post<any>(environment.serviceConfigClassImage + `/change-class-image/${classImageId}`, formData, {
                headers: new HttpHeaders({accept: '*/*'})
            })
            .pipe(
                catchError(this.handleServiceError)
            );
    }

    changeClassAudio(classImageId: number, audio: File): Observable<any> {
        const formData = new FormData();
        formData.append('audio', audio, audio.name);

        return this._http
            .post<any>(environment.serviceConfigClassImage + `/change-class-audio/${classImageId}`, formData, {
                headers: new HttpHeaders({accept: '*/*'})
            })
            .pipe(
                catchError(this.handleServiceError)
            );
    }

    getConfigExamById(examId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigExam + `/${examId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateConfigExam(configExam: Exam): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceConfigExam, configExam, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    insertConfigExam(configExam: Exam): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceConfigExam, configExam, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteExam(examId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceConfigExam + `/${examId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    getConfigExamByModuleId(moduleId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigExam + `/by-module/${moduleId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getConfigReferenceById(examId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigReference + `/${examId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateConfigReference(configReference: Reference): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceConfigReference, configReference, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    insertConfigReference(configReference: Reference): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceConfigReference, configReference, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteConfigReference(referenceId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceConfigReference + `/${referenceId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    getConfigReferencesByModuleId(moduleId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigReference + `/by-module/${moduleId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener un curso con el objeto minimo de datos */
    geShortByIdStudent(courseId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceStudentCourse + `/short/${courseId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener el curso de un estudiante (aplicante) por su id */
    getStudentCourseById(studentCourseId: number): Observable<Course> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<Course>(environment.serviceStudentCourse + `/${studentCourseId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getClassWithNavigation(classId: number, currentImageId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigClassImage + `/class-navegation/${classId}/${currentImageId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getImageWithNavigation(classId: number, imageId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigClassImage + `/image-navegation/${classId}/${imageId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getAllClassImageByClassId(classId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigClassImage + `/by-class/${classId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateClassStatus(classId: number, status: boolean, currentImageId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceStudentClass + `/${classId}/${status}/${currentImageId}`, {
                headers,
            })
            .pipe(
                map((res) => res),
                catchError(this.handleServiceError)
            );
    }

    getPhoto(filename: string): Observable<any> {
        const headers = new HttpHeaders({
            accept: '*/*',
        });

        return this._http
            .get<Blob>(environment.serviceImagenes + '?filename=' + `${filename}`, {
                headers: headers,
                responseType: "blob" as "json"
            })
            .pipe(
                map(data => this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(data))),
                catchError(this.handleServiceError)
            );
    }

    getExamWithQuestionAndOptions(examId: number, totalQuestions: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigExamQuestion + `/${examId}` + `/${totalQuestions}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getAllQuestionByExamId(examId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigExamQuestion + `/by-exam/${examId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getQuestionById(questionId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceConfigExamQuestion + `/${questionId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getQuestionOptionById(optionId: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceQuestionOption + `/${optionId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    updateQuestionOption(questionOption: QuestionOption): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceQuestionOption, questionOption, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    insertQuestionOption(questionOption: QuestionOption): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceQuestionOption, questionOption, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteQuestionOption(optionId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceQuestionOption + `/${optionId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    updateConfigExamQuestion(configExam: Question): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceConfigExamQuestion, configExam, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    insertConfigQuestion(configExam: Question): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceConfigExamQuestion, configExam, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    deleteConfigQuestion(questionId: number): Observable<void> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .delete<any>(environment.serviceConfigExamQuestion + `/${questionId}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError),
            );
    }

    submitExamAnswers(examId: number, answers: ExamResult[]): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .put<any>(environment.serviceStudentExam + `/${examId}`, answers, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    submitFeedback(form: StudentFeedback): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .post<any>(environment.serviceStudentFeedback, form, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    getFeedback(courseId?: number): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });

        return this._http
            .get<any>(environment.serviceStudentFeedback + `/by-course/${courseId}`, {headers: headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

    /** Obtener modulos de un curso */
    getAllDocumentsByLanguageAndDocName(docName: string): Observable<any> {
        const headers = new HttpHeaders({
            accept: 'application/json',
        });
        return this._http
            .get<any>(environment.serviceTemas + `${docName}`, {headers})
            .pipe(
                map((data) => data),
                catchError(this.handleServiceError)
            );
    }

}
